'use client'
import { useContext, useEffect, useRef, useState } from 'react'
import { BasketContext } from '../BasketProvider'
import { useUser } from '@/eshop/hooks/useUser'
import { getUserProductsPricesAndAvailability } from '@/eshop/queries'
import { Future, Result } from '@swan-io/boxed'
import { useK2Setup } from '@/eshop/hooks/useK2Setup'
import { RequestError } from '@/utils/request'
import { PricesAndAvailabilityResult } from '@/eshop/gql/graphql'

export function useUpdateBasketPrice() {
    const { products, changeProductPrice, setIsLoading, isLoading } =
        useContext(BasketContext)
    const user = useUser()
    const k2 = useK2Setup()
    const priceCache = useRef<{
        timestamp: number
        productIds: string[]
    }>({
        timestamp: 0,
        productIds: [],
    })

    const [responseState, setResponseState] = useState<
        'ok' | 'error' | 'loading' | null
    >(null)

    useEffect(() => {
        const lastCheck = Date.now() - priceCache.current.timestamp
        const productIds = products.map((p) => p.id)

        if (
            products.length === 0 ||
            !user ||
            user?.isAnonymous ||
            (lastCheck < 15 * 60 * 1000 &&
                productIds.every((pid) =>
                    priceCache.current.productIds.includes(pid)
                ))
        ) {
            return
        }
        setIsLoading(true)
        setResponseState('loading')
        priceCache.current = {
            timestamp: Date.now(),
            productIds,
        }

        async function getPrices() {
            const groupNames = new Set<string>()
            products.forEach((p) => {
                groupNames.add(p.name)
            })

            const prices = await Future.allFromDict(
                [...groupNames].reduce(
                    (acc, group) => {
                        const manufacturers = products
                            .filter((p) => p.name === group)
                            .map((p) => p.manufacturer)

                        const uniqManufacturers = new Set(manufacturers)

                        acc[group] = getUserProductsPricesAndAvailability()({
                            sortingName: group,
                            currency: k2.currency,
                            languageId: k2.languageId,
                            brand:
                                uniqManufacturers.size === 1
                                    ? manufacturers[0]
                                    : undefined,
                        })
                        return acc
                    },
                    {} as Record<
                        string,
                        Future<
                            Result<
                                {
                                    pricesAndAvailabilityUser: PricesAndAvailabilityResult
                                },
                                RequestError<any>
                            >
                        >
                    >
                )
            )
                .map(Result.allFromDict)
                .toPromise()

            prices.match({
                Ok: (r) => {
                    products.forEach((p) => {
                        if (!r[p.name]) {
                            return
                        }
                        const pp = r[
                            p.name
                        ].pricesAndAvailabilityUser.products?.find(
                            (prod) => prod?.abbr === p.catNo
                        )
                        if (!pp) {
                            return
                        }
                        changeProductPrice(p.id, pp.priceBrutto, pp.priceNetto)
                    })

                    setIsLoading(false)
                    setResponseState('ok')
                },
                Error: (e) => {
                    setIsLoading(false)
                    setResponseState('error')
                },
            })
        }

        getPrices()
    }, [products, user, k2, changeProductPrice, setIsLoading])
    return responseState
}
